import { sum } from 'lodash'
import {
  CostType,
  PricingModelFragment,
  ProjectVersionFragment,
} from '../../generated/graphql'
import { getProviderIdsToMultipliers } from '../../project-versions/version-util'
import { groupBy, reduceToSumOfItems } from '../../util/array-utility'
import { VersionCalculator } from '../VersionCalculator'
import {
  ContainerCostCalculator,
  createMapContainerToCostCalculator,
} from './ContainerCostCalculator'
import {
  createMapCostToCalculatedCost,
  CalculatedEnergyCost,
  CalculatedOtherCost,
} from './cost-calculator'
import type { OtherCostValues } from '../../costs/create-cost-export'

export class VersionCostCalculator {
  private readonly containers: ContainerCostCalculator[] = []

  private readonly costs: CalculatedOtherCost[] = []

  private readonly nonMulpliedCosts: CalculatedOtherCost[] = []

  public constructor(
    private readonly version: ProjectVersionFragment,
    private readonly pricingModel: PricingModelFragment,
  ) {}

  public addContainer(
    container: ContainerCostCalculator,
  ): VersionCostCalculator {
    this.containers.push(container)
    return this
  }

  public addCosts(costs: CalculatedOtherCost[]): VersionCostCalculator {
    this.costs.push(...costs)
    return this
  }

  public addNonMultipliedCosts(
    costs: CalculatedOtherCost[],
  ): VersionCostCalculator {
    this.nonMulpliedCosts.push(...costs)
    return this
  }

  public getId(): string {
    return this.version.id
  }

  public getName(): string {
    return this.version.name
  }

  public getInspectionLengthInYears(): number {
    return this.pricingModel.inspectionLengthInYears
  }

  public getBuildingYear(): number {
    return this.pricingModel.buildingYear
  }

  public getContainers(): ContainerCostCalculator[] {
    return this.containers
  }

  public calculateCosts(): void {
    this.containers.forEach((container) => {
      container.calculateCosts()
    })
  }

  public getEnergyCosts(containerId?: string): CalculatedEnergyCost[] {
    if (containerId) {
      return (
        this.containers
          .find((container) => container.getId() === containerId)
          ?.getEnergyCosts() ?? []
      )
    }
    return this.containers.flatMap((container) => container.getEnergyCosts())
  }

  public getYearlyEnergyCostValues(containerId?: string): [number, number][] {
    return this.getEnergyCosts(containerId)
      .map((cost) => cost.chartValues)
      .reduce(reduceToSumOfItems, [])
      .map(this.mapValueToYearlyValue.bind(this))
  }

  public getYearlyOtherCostValues(containerId?: string): [number, number][] {
    return this.getCosts(containerId)
      .map((cost) => cost.chartValues)
      .reduce(reduceToSumOfItems, [])
      .map(this.mapValueToYearlyValue.bind(this))
  }

  public getYearlyEnergyValuesForExport(): number[] {
    return this.getEnergyCosts()
      .map((cost) => cost.chartValues)
      .reduce(reduceToSumOfItems, [])
  }

  public getYearlyOtherValuesForExport(): OtherCostValues[] {
    const group = groupBy(
      this.getCosts(),
      (calculatedCost) => calculatedCost.type,
    )
    return Object.keys(group).map((costType) => ({
      costType: costType as CostType,
      values: group[costType]
        .map((calculateCost) => calculateCost.chartValues)
        .reduce(reduceToSumOfItems, []),
    }))
  }

  public getAllCumulativeYearlyCostValues(
    containerId?: string,
  ): [number, number][] {
    return this.getAllCumulativeCostValues(containerId).map(
      this.mapValueToYearlyValue.bind(this),
    )
  }

  public getCumulativeEnergySum(containerId?: string): number {
    return this.getEnergyCosts(containerId).reduce(
      (total, cost) => total + sum(cost.chartValues),
      0,
    )
  }

  public getCumulativeOtherSum(
    types?: CostType[],
    containerId?: string,
  ): number {
    return this.getCosts(containerId)
      .filter((cost) => (types ? types.includes(cost.type) : true))
      .reduce((total, cost) => total + sum(cost.chartValues), 0)
  }

  public getCumulativeSum(containerId?: string): number {
    return (
      this.getCumulativeEnergySum(containerId) +
      this.getCumulativeOtherSum(undefined, containerId)
    )
  }

  public getAllCumulativeCostValues(containerId?: string): number[] {
    const reduceToCumulativeValues = (
      cumulativeValues: number[],
      currentValue: number,
      index: number,
    ) => {
      const previousValue = index > 0 ? cumulativeValues[index - 1] : 0
      cumulativeValues.push(previousValue + currentValue)
      return cumulativeValues
    }
    return [...this.getEnergyCosts(containerId), ...this.getCosts(containerId)]
      .map((cost) => cost.chartValues)
      .reduce(reduceToSumOfItems, [])
      .reduce<number[]>(reduceToCumulativeValues, [])
  }

  private mapValueToYearlyValue(
    value: number,
    index: number,
  ): [number, number] {
    return [this.pricingModel.buildingYear + index, value]
  }

  private getCosts(containerId?: string): CalculatedOtherCost[] {
    if (containerId) {
      const providerIds = this.getContainers()
        .find((container) => container.getId() === containerId)
        ?.getProviderIds()
      return providerIds
        ? this.nonMulpliedCosts.filter((cost) =>
            cost.providerId ? providerIds.includes(cost.providerId) : false,
          )
        : ([] as CalculatedOtherCost[])
    }
    return this.costs
  }
}

export function mapVersionToCostCalculator(
  versionEnergyCalculator: VersionCalculator,
  version: ProjectVersionFragment,
  pricingModel: PricingModelFragment,
): VersionCostCalculator {
  const versionCostCalculator = new VersionCostCalculator(version, pricingModel)

  const containers = versionEnergyCalculator.getAllContainers()

  const mapContainerToCostCalculator =
    createMapContainerToCostCalculator(pricingModel)
  const containerCostCalculators = containers.map(mapContainerToCostCalculator)

  containerCostCalculators.forEach((container) => {
    versionCostCalculator.addContainer(container)
  })

  const providerIdsToBuildingMultipliers = version.buildings
    ? getProviderIdsToMultipliers([version])
    : {}

  if (version.costs) {
    const mapCostToCalculatedCost = createMapCostToCalculatedCost(pricingModel)
    const costs = version.costs.map((cost) =>
      mapCostToCalculatedCost(cost, providerIdsToBuildingMultipliers),
    )
    versionCostCalculator.addCosts(costs)
    const nonMultipliedCosts = version.costs.map((cost) =>
      mapCostToCalculatedCost(cost, {}),
    )
    versionCostCalculator.addNonMultipliedCosts(nonMultipliedCosts)
  }

  return versionCostCalculator
}

export function getContainerId(
  versionCalculator: VersionCostCalculator,
  containerIdsToNames: Record<string, string>,
  selectedContainerName: string,
): string | undefined {
  return versionCalculator
    .getContainers()
    .find(
      (container) =>
        containerIdsToNames[container.getId()] === selectedContainerName,
    )
    ?.getId()
}
